import { Controller } from "@hotwired/stimulus"

// Bootstrap Icons' check-lg, drawn in the same 16x16 viewBox as the link-45deg it replaces.
const CHECK_ICON_PATH = '<path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z"/>'

// The kind of the Turbo visit that rendered the page: "restore" for Back/Forward.
let lastVisitAction = null
document.addEventListener("turbo:visit", (event) => { lastVisitAction = event.detail.action })

// A URL fragment, percent-decoded; as given when it is not valid percent-encoding ("%" alone).
function decodeFragment(fragment) {
  try { return decodeURIComponent(fragment) } catch { return fragment }
}

// A heading id or fragment with its letters decomposed, the diacritics dropped and lower-cased:
// WikiPageTocBuilder's slug keeps letters and digits only (so a combining mark from text stored
// decomposed is lost) and lower-cases, and a link typed elsewhere may lose its ă, ș or ѓ.
function foldAnchor(text) {
  return text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase()
}

// Whether the browser itself scrolls to this fragment on arrival: an element with that id, or
// an <a name> (the guides' source list, as the Action Text sanitizer drops id but keeps name).
function indicatedElement(fragment) {
  return document.getElementById(fragment) ||
    [...document.getElementsByName(fragment)].find((element) => element.tagName === "A")
}

export default class extends Controller {
  static targets = [
    "tocPanel",
    "tocLink",
    "content",
    "backToTop",
    "mobileToggle",
    "mobileBreadcrumb",
    "mobileBreadcrumbEyebrow",
    "mobileBreadcrumbTitle",
    "backdrop",
    "drawer"
  ]
  static values = {
    copiedLabel: String,
    chapterAbbr: { type: String, default: "Ch." }
  }

  connect() {
    this.currentHeadingId = null
    this.ticking = false
    this.scrollContainer = document.querySelector(".native-app-content") || window

    this.setupScrollSpy()
    this.setupBackToTop()
    this.setupTooltips()
    this.setupHeadingAnchors()
    this.boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
    this.boundHashChange = () => this.flashCurrentHash()
    window.addEventListener("hashchange", this.boundHashChange)
    this.boundArrival = () => this.scrollToArrivalHash()
    document.addEventListener("turbo:load", this.boundArrival)
    this.flashCurrentHash()
    // Without Turbo (the apex wiki bundle, wiki.js) turbo:load never fires: every arrival is a
    // full page load, and the page's controllers connect once it is parsed. Not on Back/Forward
    // or a reload, where the browser puts back the position the visitor had scrolled to.
    if (!window.Turbo && this.freshNavigation) this.scrollToArrivalHash()
  }

  get freshNavigation() {
    const type = performance.getEntriesByType?.("navigation")?.[0]?.type
    return type === undefined || type === "navigate"
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.tooltip) this.tooltip.remove()
    this.scrollContainer.removeEventListener("scroll", this.boundScroll)
    document.removeEventListener("keydown", this.boundKeydown)
    window.removeEventListener("hashchange", this.boundHashChange)
    document.removeEventListener("turbo:load", this.boundArrival)
  }

  // --- Scroll Spy ---

  setupScrollSpy() {
    if (!this.hasContentTarget) return

    const headings = this.contentTarget.querySelectorAll("h2[id], h3[id]")
    if (headings.length === 0) return

    const observerOptions = { rootMargin: "-80px 0px -70% 0px" }
    if (this.scrollContainer !== window) observerOptions.root = this.scrollContainer

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setActiveLink(entry.target)
          }
        })
      },
      observerOptions
    )

    headings.forEach((heading) => this.observer.observe(heading))
  }

  setActiveLink(heading) {
    const headingId = heading.id
    if (this.currentHeadingId === headingId) return
    this.currentHeadingId = headingId

    this.tocLinkTargets.forEach((link) => {
      const isActive = link.getAttribute("href") === `#${headingId}`
      link.classList.toggle("toc-active", isActive)

      if (isActive && this.hasTocPanelTarget) {
        this.scrollTocLinkIntoView(link)
      }
    })

    const isChapterHeading = heading.tagName === "H2"
    const chapterId = isChapterHeading ? headingId : (heading.dataset.chapterId || null)
    this.updateMobileBreadcrumb(heading, chapterId)
  }

  updateMobileBreadcrumb(heading, chapterId) {
    if (!this.hasMobileBreadcrumbTitleTarget) return

    const isChapterHeading = heading.tagName === "H2"
    let chapterNumber = null
    let articleNumber = null
    let title = heading.textContent.replace(/#$/, "").trim()

    if (isChapterHeading) {
      chapterNumber = heading.dataset.chapterNumber
    } else {
      articleNumber = heading.dataset.articleNumber
      const chapterHeading = chapterId ? this.contentTarget.querySelector(`h2[id="${chapterId}"]`) : null
      if (chapterHeading) chapterNumber = chapterHeading.dataset.chapterNumber
    }

    const eyebrowParts = []
    if (chapterNumber) eyebrowParts.push(`${this.chapterAbbrValue} ${chapterNumber}`)
    if (articleNumber) eyebrowParts.push(`§${articleNumber}`)

    if (this.hasMobileBreadcrumbEyebrowTarget) {
      this.mobileBreadcrumbEyebrowTarget.textContent = eyebrowParts.join(" · ")
    }
    this.mobileBreadcrumbTitleTarget.textContent = this.cleanHeadingText(heading)
  }

  cleanHeadingText(heading) {
    const clone = heading.cloneNode(true)
    clone.querySelectorAll("a.heading-anchor, .heading-anchor-label").forEach((n) => n.remove())
    return clone.textContent.trim()
  }

  scrollTocLinkIntoView(link) {
    const panel = this.tocPanelTarget
    if (!panel.contains(link)) return

    const linkRect = link.getBoundingClientRect()
    const panelRect = panel.getBoundingClientRect()

    if (linkRect.top < panelRect.top || linkRect.bottom > panelRect.bottom) {
      const offset = link.offsetTop - panel.offsetTop - (panel.clientHeight / 2) + (link.clientHeight / 2)
      panel.scrollTo({ top: offset, behavior: "smooth" })
    }
  }

  // --- Smooth Scrolling ---

  scrollTo(event) {
    event.preventDefault()
    this.scrollToHref(event.currentTarget.getAttribute("href"))
  }

  // Links in the body to a heading of the same page — the sign numbers in a road-sign
  // note ("знаки 1.1, 1.2 и 1.5–1.34"). Left to the browser, Turbo takes the click and a
  // fragment in Cyrillic or Georgian goes nowhere: no scroll, no URL change. So they take
  // the table of contents' path. The heading's own anchor keeps its copy-link behaviour.
  followAnchor(event) {
    const link = event.target.closest("a[href^='#']")
    if (!link || link.classList.contains("heading-anchor")) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    if (this.scrollToHref(link.getAttribute("href"))) event.preventDefault()
  }

  // Returns whether the href named a heading on this page.
  scrollToHref(href) {
    if (!href || !href.startsWith("#")) return false
    // getElementById tolerates ids starting with a digit; querySelector("#11...")
    // would throw SyntaxError because CSS identifiers can't begin with a digit.
    const target = document.getElementById(decodeFragment(href.slice(1)))
    if (!target) return false

    const container = this.scrollContainer
    const offset = 80
    const distance = Math.abs(target.getBoundingClientRect().top)
    const behavior = distance > 3000 ? "instant" : "smooth"

    if (container === window) {
      const top = target.getBoundingClientRect().top + window.scrollY - offset
      window.scrollTo({ top, behavior })
    } else {
      const top = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - offset
      container.scrollTo({ top, behavior })
    }

    history.replaceState(null, "", href)
    this.flashTarget(target)

    if (this.isMobileOpen) this.closeMobile()
    return true
  }

  // Arriving from another page at a heading the fragment does not name exactly. Turbo looks a
  // percent-encoded fragment up as it is ("…-velocit%C3%A0-kmh", a road-sign note's link to
  // another topic page), finds nothing and leaves the page at the top; the browser decodes it
  // first, but neither matches a fragment whose letters are composed differently from the id
  // (ă as a + U+0306 against the id's single ă, ѓ likewise) or that lost its diacritics or
  // case on the way. Those land on the heading they mean (headingFor). Fragments found as
  // given are left to Turbo or the browser, and Turbo's Back/Forward keeps the position it
  // restores. scrollIntoView honours the headings' scroll-margin-top (scroll-mt-24 under the
  // sticky header, more for article headings), so the heading lands where a fragment the
  // browser resolves itself would. An instant jump, as Turbo makes for those: the page above
  // is still growing at this point, and the browser's scroll anchoring keeps the heading in
  // place only when no smooth scroll is running.
  scrollToArrivalHash() {
    const raw = window.location.hash.slice(1)
    if (!raw || lastVisitAction === "restore" || indicatedElement(raw)) return

    const decoded = decodeFragment(raw)
    // The browser also finds the decoded fragment, and has scrolled there already.
    if (!window.Turbo && indicatedElement(decoded)) return

    const heading = document.getElementById(decoded) || this.headingFor(decoded)
    if (!heading) return

    heading.scrollIntoView({ block: "start" })
    if (heading.id === decoded) return

    // The address names the heading itself from now on, so a copied link finds it. The
    // section is flashed here: flashCurrentHash ran on connect, when the address did not.
    history.replaceState(history.state, "", `#${heading.id}`)
    this.flashTarget(heading)
  }

  // The heading whose id the fragment means: the same text in canonical composition (NFC),
  // else the same once both are folded (foldAnchor). The first in page order on a tie.
  headingFor(fragment) {
    const headings = [...this.element.querySelectorAll("h2[id], h3[id]")]
    const composed = fragment.normalize("NFC")
    const folded = foldAnchor(fragment)
    return headings.find((heading) => heading.id.normalize("NFC") === composed) ||
      headings.find((heading) => foldAnchor(heading.id) === folded)
  }

  flashCurrentHash() {
    const hash = window.location.hash
    if (!hash || hash.length < 2) return
    const target = document.getElementById(decodeFragment(hash.slice(1)))
    if (target) this.flashTarget(target)
  }

  flashTarget(heading) {
    const elements = this.collectSectionContent(heading)
    elements.forEach((el) => {
      el.classList.remove("wiki-anchor-flash")
      // Force reflow so the animation re-triggers on repeat clicks of the same anchor.
      void el.offsetWidth
      el.classList.add("wiki-anchor-flash")
    })
  }

  // Block siblings after the heading up to (but not including) the next h1/h2/h3.
  // Falls back to the heading itself when the section has no body. Image/media
  // blocks are skipped — highlighting them produces ugly slivers around their
  // bounding box rather than reading as part of the section text.
  collectSectionContent(heading) {
    const skipTags = new Set(["IMG", "FIGURE", "ACTION-TEXT-ATTACHMENT", "PICTURE", "VIDEO", "IFRAME"])
    const elements = []
    let cursor = heading.nextElementSibling
    while (cursor && !["H1", "H2", "H3"].includes(cursor.tagName)) {
      if (!skipTags.has(cursor.tagName)) elements.push(cursor)
      cursor = cursor.nextElementSibling
    }
    return elements.length > 0 ? elements : [heading]
  }

  // --- Back to Top ---

  setupBackToTop() {
    this.boundScroll = () => {
      if (!this.ticking) {
        requestAnimationFrame(() => {
          this.updateBackToTop()
          this.ticking = false
        })
        this.ticking = true
      }
    }
    this.scrollContainer.addEventListener("scroll", this.boundScroll, { passive: true })
    this.updateBackToTop()
  }

  updateBackToTop() {
    if (!this.hasBackToTopTarget) return
    const scrollY = this.scrollContainer === window ? window.scrollY : this.scrollContainer.scrollTop
    this.backToTopTarget.classList.toggle("hidden", scrollY <= 400)
  }

  scrollToTop(event) {
    event.preventDefault()
    this.scrollContainer.scrollTo({ top: 0, behavior: "smooth" })
  }

  // --- Mobile TOC Drawer (bottom sheet) ---

  get isMobileOpen() {
    if (!this.hasTocPanelTarget) return false
    return this.tocPanelTarget.classList.contains("toc-drawer-open")
  }

  toggleMobile() {
    if (this.isMobileOpen) {
      this.closeMobile()
    } else {
      this.openMobile()
    }
  }

  openMobile() {
    if (!this.hasTocPanelTarget) return
    this.tocPanelTarget.classList.add("toc-drawer-open")
    if (this.hasBackdropTarget) this.backdropTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden", "lg:overflow-auto")
  }

  closeMobile() {
    if (!this.hasTocPanelTarget) return
    this.tocPanelTarget.classList.remove("toc-drawer-open")
    if (this.hasBackdropTarget) this.backdropTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden", "lg:overflow-auto")
  }

  // --- Tooltips for truncated links ---

  setupTooltips() {
    if (this.isNativeApp) return

    this.tooltip = document.createElement("div")
    this.tooltip.className = "toc-tooltip"
    document.body.appendChild(this.tooltip)

    this.tocLinkTargets.forEach((link) => {
      if (link.scrollWidth > link.clientWidth) {
        link.addEventListener("mouseenter", () => this.showTooltip(link))
        link.addEventListener("mouseleave", () => this.hideTooltip())
      }
    })
  }

  get isNativeApp() {
    return navigator.userAgent.includes("Hotwire Native")
  }

  showTooltip(link) {
    const rect = link.getBoundingClientRect()
    this.tooltip.textContent = link.textContent.trim()
    this.tooltip.classList.add("visible")
    const maxLeft = window.innerWidth - this.tooltip.offsetWidth - 8
    const left = Math.max(8, Math.min(rect.left, maxLeft))
    this.tooltip.style.left = `${left}px`
    this.tooltip.style.top = `${rect.bottom + 4}px`
  }

  hideTooltip() {
    this.tooltip.classList.remove("visible")
  }

  // --- Heading anchor link copy ---

  setupHeadingAnchors() {
    if (!this.hasContentTarget) return

    this.contentTarget.querySelectorAll("a.heading-anchor").forEach((anchor) => {
      anchor.addEventListener("click", (event) => this.copyHeadingLink(event, anchor))
    })
  }

  copyHeadingLink(event, anchor) {
    event.preventDefault()

    const heading = anchor.closest("h2, h3")
    if (!heading || !heading.id) return

    const url = `${window.location.origin}${window.location.pathname}${window.location.search}#${heading.id}`
    history.pushState(null, "", url)
    this.flashTarget(heading)

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(url).then(() => this.flashCopied(anchor))
    } else {
      this.fallbackCopy(url)
      this.flashCopied(anchor)
    }
  }

  // The anchor's icon turns into a check mark for three seconds. The slim apex wiki head
  // draws it as an inline SVG (it loads no icon font), so there the drawing is swapped;
  // elsewhere the font glyph's class. The original is kept from the first click, so a
  // second click within the three seconds does not leave the check mark for good.
  flashCopied(anchor) {
    const svg = anchor.querySelector("svg")
    const icon = svg || anchor.querySelector("i")
    if (!icon) return

    if (anchor._originalIcon === undefined) anchor._originalIcon = svg ? svg.innerHTML : icon.className
    if (svg) svg.innerHTML = CHECK_ICON_PATH; else icon.className = "bi bi-check-lg"
    anchor.classList.add("copied")

    let label = anchor.querySelector(".heading-anchor-label")
    if (!label) {
      label = document.createElement("span")
      label.className = "heading-anchor-label"
      anchor.appendChild(label)
    }
    label.textContent = this.copiedLabelValue || "Copied"

    clearTimeout(anchor._copiedTimer)
    anchor._copiedTimer = setTimeout(() => {
      if (svg) svg.innerHTML = anchor._originalIcon; else icon.className = anchor._originalIcon
      anchor._originalIcon = undefined
      anchor.classList.remove("copied")
      label.remove()
    }, 3000)
  }

  fallbackCopy(text) {
    const textarea = document.createElement("textarea")
    textarea.value = text
    textarea.style.position = "fixed"
    textarea.style.opacity = "0"
    document.body.appendChild(textarea)
    textarea.select()
    try { document.execCommand("copy") } catch (_) { /* noop */ }
    textarea.remove()
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.isMobileOpen) {
      this.closeMobile()
    }
  }
}
